import React, { useRef } from 'react';

import { feedbackMessages } from '../../../translates/feedbackMessages';
import { generalMessages } from '../../../translates/generalMessages';
import { useIntlReexport } from '../../hooks/useIntlReexport';
import { useNetwork } from '../../hooks/useNetwork';
import { useSwitch } from '../../hooks/useSwitch';
import { sendFeedbackFormApi } from '../../store/middleware/apis/feedbackFormApi';
import { getCurrentContentItem } from '../../store/selectors/getCurrentContentItem';
import { getCurrentItem } from '../../store/selectors/getCurrentItem';
import { useStudentSelector } from '../../store/selectors/useStudentSelector';
import { AssertionException } from '../../utils/log';
import { ContentWrapper } from '../ContentWrapper/ContentWrapper';
import { ModalType } from '../ContentWrapper/Modal/Modal';
import { ModalDialog } from '../modal/ModalDialog/ModalDialog';
import { ModalDialogFooter } from '../modal/ModalDialog/ModalDialogFooter';
import { Spinner } from '../shared/Spinner';
import { ClientEmailConfirmation } from './ClientEmailConfirmation';
import { FeedbackModalAttachment } from './FeedbackModalAttachment';
import { getLastItemInArray, isStringEmptyOrWhiteSpace, megabytesToBytes } from '@ael/core-api';
import { Button, ButtonVariant, useToastNotifications } from '@ael/shared-components-library';
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { List } from 'immutable';
import { FormattedMessage } from 'react-intl';

import './FeedbackModal.scss';

const MODAL_Z_INDEX = 1300;
const MAX_FILES_NUMBER = 3;

const MAX_FILE_SIZE_IN_MB = 15;
const MAX_FILES_SIZE_TEXT = `${MAX_FILE_SIZE_IN_MB}MB`;

const MAX_FILE_SIZE_IN_BYTES = megabytesToBytes(MAX_FILE_SIZE_IN_MB);

const MAX_BODY_LENGTH = 2000;

const ALLOWED_EXTENSIONS = ['png', 'jpg', 'jpeg', 'mp4'];

export interface FeedbackModalProps {
  onClose: () => void;
  emailAddress: string;
  projectReference: string;
  projectName: string;
  backendUrl: string;
  courseGuid: string;
}

enum FormFieldNames {
  Title = 'title',
  IssueDescription = 'issueDescription',
  StudentEmail = 'studentEmail',
  Files = 'files',
  SlideReference = 'slideReference',
}

export const FeedbackModal: React.FC<FeedbackModalProps> = ({
  onClose,
  emailAddress,
  projectReference,
  projectName,
  backendUrl,
  courseGuid,
}: FeedbackModalProps) => {
  const { formatMessage } = useIntlReexport();
  const isOnline = useNetwork();
  const formRef = React.useRef<HTMLFormElement>(null);
  const submitButtonRef = React.useRef<HTMLInputElement>(null);
  const toolbarRef = useRef(null);

  const [attachments, setAttachments] = React.useState<List<File> | null>(null);
  const [body, setBody] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [attachmentsErrorMessages, setAttachmentsErrorMessages] = React.useState<string[]>([]);
  const { isOn: showClientConfirmation, toggle: toggleShowClientConfirmation } = useSwitch(false);

  const { addSuccessToast } = useToastNotifications();

  const currentItem = useStudentSelector(getCurrentItem);
  const currentContentItem = useStudentSelector(getCurrentContentItem);

  const resetForm = React.useCallback(() => {
    setAttachments(null);
    if (formRef.current) {
      formRef.current.reset();
    }
  }, []);

  const formValidation = React.useCallback(() => {
    setAttachmentsErrorMessages([]);
    if (attachments && attachments.size > 0) {
      if (attachments.size > 3) {
        setAttachmentsErrorMessages((prev) =>
          prev.concat(formatMessage(feedbackMessages.maximumNumberOfFiles, { fileLimit: MAX_FILES_NUMBER }))
        );
      }
      const sizeOfAttachments: number = attachments.reduce((size: number, attachment: File) => {
        if (size !== undefined) {
          return size + attachment.size;
        }
        return attachment.size;
      }, 0);
      if (sizeOfAttachments > MAX_FILE_SIZE_IN_BYTES) {
        setAttachmentsErrorMessages((prev) =>
          prev.concat(formatMessage(feedbackMessages.maximumFileSize, { fileSizeLimit: MAX_FILES_SIZE_TEXT }))
        );
      }

      attachments.forEach((attachment: File) => {
        const fileExtension = getLastItemInArray(attachment.name.split('.')).toLowerCase();

        if (!ALLOWED_EXTENSIONS.includes(fileExtension)) {
          setAttachmentsErrorMessages((prev) =>
            prev.concat(formatMessage(feedbackMessages.invalidFileFormat, { fileExtension: fileExtension }))
          );
        }
      });
    }
  }, [attachments, formatMessage]);

  const bodyValidation = React.useCallback(() => {
    if (body.length > MAX_BODY_LENGTH) {
      setAttachmentsErrorMessages((prev) => prev.concat(formatMessage(feedbackMessages.maxBodyLength)));
    }
  }, [body.length, formatMessage]);

  React.useEffect(() => {
    formValidation();
  }, [formValidation]);
  React.useEffect(() => {
    bodyValidation();
  }, [bodyValidation]);

  const generateTitle = React.useMemo(() => {
    let title = '';
    if (currentItem?.title) {
      title += `Selected item: "${currentItem.title}". `;
    }
    if (currentContentItem) {
      title += `Selected content item: "${currentContentItem.title}".`;
    }
    if (title === '') {
      title = 'No item selected.';
    }
    return title;
  }, [currentContentItem, currentItem]);

  const onFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newAttachments = event.target.files ? List<File>(event.target.files) : null;
    if (newAttachments) {
      setAttachments((prev) => (prev ? prev.concat(newAttachments).toList() : newAttachments));
    }
  };

  const itemReference = React.useMemo(
    () => (currentContentItem ? `${projectReference}-${currentContentItem.referenceIdentifier}` : ''),
    [currentContentItem, projectReference]
  );

  const formData = React.useCallback(() => {
    if (!formRef.current) {
      throw AssertionException('Cannot find form.');
    }

    const formData = new FormData(formRef.current);
    formData.delete(FormFieldNames.Files);
    formData.append(FormFieldNames.Title, generateTitle);
    formData.append(FormFieldNames.SlideReference, itemReference);

    if (attachments) {
      attachments.forEach((file: File) => {
        formData.append(FormFieldNames.Files, file);
      });
    }
    return formData;
  }, [attachments, generateTitle, itemReference]);

  const doneSubmitting = React.useCallback(() => {
    resetForm();
    setSubmitting(false);
    onClose();
  }, [onClose, resetForm]);

  const sendEmailViaClient = React.useCallback(() => {
    toggleShowClientConfirmation();
    const subject = `${formatMessage(generalMessages.platformName)}: Feedback on course "${projectName}"`;
    const issueDescription = formData().get(FormFieldNames.IssueDescription)?.toString() ?? '';
    const body = `Message: ${issueDescription} %0D%0A%0D%0A Location: ${generateTitle} %0D%0A%0D%0A Reference number: ${itemReference}`;
    const link = `mailto:${emailAddress}?subject=${subject}&body=${body}`;
    window.location.href = link;
    doneSubmitting();
  }, [
    emailAddress,
    projectName,
    doneSubmitting,
    formData,
    formatMessage,
    generateTitle,
    itemReference,
    toggleShowClientConfirmation,
  ]);

  const triggerSubmitButtonClick = React.useCallback(() => {
    if (submitButtonRef.current) {
      submitButtonRef.current.click();
    }
  }, []);

  const formSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (hasError) {
      return;
    }
    await sendEmail();
  };

  const sendEmail = async () => {
    setSubmitting(true);
    if (isOnline) {
      const response = await sendFeedbackFormApi(backendUrl, courseGuid, formData());
      if (response.status === 200) {
        addSuccessToast({ title: formatMessage(feedbackMessages.thankYou) });
        doneSubmitting();
        return;
      }
    }
    // try to send email via client when sending via API fails, or when user is offline
    toggleShowClientConfirmation();
    return;
  };

  const removeFileFromFileList = React.useCallback((fileIndex: number) => {
    setAttachments((prev) => (prev ? prev.delete(fileIndex) : null));
  }, []);

  const hasError = attachmentsErrorMessages.length > 0;

  return (
    <>
      <ModalDialog zIndex={MODAL_Z_INDEX}>
        <ContentWrapper alwaysShowControl>
          <ContentWrapper.Modal modalType={ModalType.verySmall} blocking>
            {submitting && (
              <div className="feedback-modal__submitting-overlay">
                <Spinner />
              </div>
            )}
            <ContentWrapper.DesktopContent noPaddingBottom>
              <ContentWrapper.Title small title={<FormattedMessage {...feedbackMessages.title} />} />
              <ContentWrapper.Body>
                <form onSubmit={(event) => void formSubmit(event)} ref={formRef}>
                  <label htmlFor="feedback-message-area" className="feedback-modal__label">
                    <FormattedMessage {...feedbackMessages.messageLabel} />
                  </label>
                  <textarea
                    id="feedback-message-area"
                    name={FormFieldNames.IssueDescription}
                    className="feedback-modal__message-area"
                    placeholder={formatMessage(feedbackMessages.messagePlaceholder)}
                    required
                    onChange={(e) => {
                      setBody(e.target.value);
                    }}
                    onKeyDown={(e) => {
                      if (
                        e.code === 'ArrowLeft' ||
                        e.code === 'ArrowRight' ||
                        e.code === 'PageUp' ||
                        e.code === 'PageDown'
                      ) {
                        e.stopPropagation();
                      }
                    }}
                  />
                  <div className="feedback-modal__slide-help-wrapper">
                    <FontAwesomeIcon icon={regular('info-circle')} className="feedback-modal__slide-help-icon" />
                    <div>{formatMessage(feedbackMessages.slideSpecific)}</div>
                  </div>
                  <div className="feedback-modal__label">
                    <FormattedMessage {...feedbackMessages.attachmentsLabel} />
                  </div>
                  <div className="feedback-modal__info">
                    <FormattedMessage
                      {...feedbackMessages.attachmentsInfo}
                      values={{
                        fileLimit: MAX_FILES_NUMBER,
                        fileSizeLimit: MAX_FILES_SIZE_TEXT,
                      }}
                    />
                  </div>
                  <label className="feedback-modal__file-input">
                    <FormattedMessage {...feedbackMessages.attachmentsButton} />
                    <input
                      name={FormFieldNames.Files}
                      type="file"
                      accept=".png,.jpg,.jpeg,.mp4"
                      onChange={onFileInputChange}
                      multiple
                    />
                  </label>
                  {attachments && (
                    <div className="feedback-modal__attachment-list">
                      {attachments.map((file: File, index: number) => (
                        <FeedbackModalAttachment
                          key={index}
                          file={file}
                          index={index}
                          onRemoveFile={removeFileFromFileList}
                        />
                      ))}
                    </div>
                  )}
                  {attachmentsErrorMessages.map((message: string, index: number) => (
                    <div key={index} className="feedback-modal__error-message">
                      {message}
                    </div>
                  ))}

                  <label htmlFor="feedback-email-input" className="feedback-modal__label">
                    <FormattedMessage {...feedbackMessages.contactLabel} />
                  </label>
                  <div className="feedback-modal__info">
                    <FormattedMessage {...feedbackMessages.contactInfo} />
                  </div>
                  <input
                    id="feedback-email-input"
                    name={FormFieldNames.StudentEmail}
                    placeholder={formatMessage(feedbackMessages.contactPlaceholder)}
                    type="email"
                    className="feedback-modal__email-input"
                  />
                  <input type="submit" hidden ref={submitButtonRef} />
                </form>
              </ContentWrapper.Body>
            </ContentWrapper.DesktopContent>
            <ContentWrapper.Toolbar ref={toolbarRef}>
              <ModalDialogFooter
                ref={toolbarRef}
                buttonsLeft={
                  <Button variant={ButtonVariant.TEXT} onClick={onClose}>
                    <FormattedMessage {...generalMessages.cancel} />
                  </Button>
                }
                buttonsRight={
                  <Button
                    variant={ButtonVariant.SUCCESS}
                    onClick={triggerSubmitButtonClick}
                    disabled={hasError || isStringEmptyOrWhiteSpace(body)}
                  >
                    <FormattedMessage {...generalMessages.send} />
                  </Button>
                }
              />
            </ContentWrapper.Toolbar>
          </ContentWrapper.Modal>
        </ContentWrapper>
      </ModalDialog>
      {showClientConfirmation && (
        <ClientEmailConfirmation
          onCancel={() => {
            toggleShowClientConfirmation();
            setSubmitting(false);
          }}
          onConfirm={sendEmailViaClient}
        />
      )}
    </>
  );
};
